import { useCallback, useEffect, useState } from 'react'
import { FileWarning, ImageOff, Maximize2, RefreshCw } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { formatDateTime } from '@/lib/datetime'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Spinner } from '@/components/ui/Spinner'
import { fetchPaymentProof } from './api'
import styles from './PaymentProofViewer.module.css'

interface PaymentProofViewerProps {
  tripRequestId: number
  hasProof: boolean
  uploadedAt: string | null
  sizeBytes: number | null
}

type State =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'missing'; message: string }
  | { status: 'error'; message: string }

/**
 * The proof-of-payment image, fetched with the operator's own credentials.
 *
 * This cannot be a plain `<img src>`. The endpoint requires an `Authorization`
 * header precisely so that a transfer receipt — which shows a customer's bank
 * account — is not readable by anyone who learns a URL. So the bytes are
 * fetched, turned into a blob URL, and revoked again when this unmounts.
 *
 * Loading is deferred until the operator asks. Fetching every receipt as the
 * drawer opens would pull images nobody looked at, and each one stays in memory
 * until revoked.
 */
export function PaymentProofViewer({ tripRequestId, hasProof, uploadedAt, sizeBytes }: PaymentProofViewerProps) {
  const [state, setState] = useState<State>({ status: 'idle' })
  const [expanded, setExpanded] = useState(false)

  // A blob URL pins its data until revoked. Without this an operator working
  // through a queue would accumulate every receipt they had opened.
  useEffect(() => {
    return () => {
      if (state.status === 'ready') URL.revokeObjectURL(state.url)
    }
  }, [state])

  // A different booking is a different image. Dropping the old one here rather
  // than leaving it on screen avoids showing one customer's receipt under
  // another customer's name for the moment before the new one arrives.
  useEffect(() => {
    setState((current) => {
      if (current.status === 'ready') URL.revokeObjectURL(current.url)
      return { status: 'idle' }
    })
    setExpanded(false)
  }, [tripRequestId])

  const load = useCallback(async () => {
    setState({ status: 'loading' })
    try {
      const { objectUrl } = await fetchPaymentProof(tripRequestId)
      setState({ status: 'ready', url: objectUrl })
    } catch (error) {
      if (error instanceof ApiError && error.isNotFound) {
        // The row says there is a proof but the file is not there. Distinct
        // from "the customer never uploaded one", and worth saying so.
        setState({ status: 'missing', message: error.message })
        return
      }
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : 'The proof of payment could not be loaded.',
      })
    }
  }, [tripRequestId])

  if (!hasProof) {
    return (
      <div className={styles.empty}>
        <ImageOff size={16} aria-hidden="true" />
        <span>No proof of payment has been uploaded yet.</span>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.meta}>
        <span>Uploaded {uploadedAt ? formatDateTime(uploadedAt) : 'at an unknown time'}</span>
        {sizeBytes ? <span className={styles.size}>{formatBytes(sizeBytes)}</span> : null}
      </div>

      {state.status === 'idle' ? (
        <Button variant="secondary" size="sm" onClick={() => void load()}>
          View proof of payment
        </Button>
      ) : null}

      {state.status === 'loading' ? (
        <div className={styles.state} role="status">
          <Spinner size="sm" label={null} />
          <span>Loading the proof of payment…</span>
        </div>
      ) : null}

      {state.status === 'missing' ? (
        <div className={styles.stateError} role="alert">
          <FileWarning size={16} aria-hidden="true" />
          <span>{state.message}</span>
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className={styles.stateError} role="alert">
          <FileWarning size={16} aria-hidden="true" />
          <span>{state.message}</span>
          <Button variant="ghost" size="sm" onClick={() => void load()} iconLeft={<RefreshCw size={14} />}>
            Try again
          </Button>
        </div>
      ) : null}

      {state.status === 'ready' ? (
        <>
          <button
            type="button"
            className={styles.thumbnailButton}
            onClick={() => setExpanded(true)}
            aria-label="Open the proof of payment at full size"
          >
            <img src={state.url} alt="Proof of payment" className={styles.thumbnail} />
            <span className={styles.expandHint} aria-hidden="true">
              <Maximize2 size={14} />
            </span>
          </button>

          <Dialog
            open={expanded}
            onClose={() => setExpanded(false)}
            title="Proof of payment"
            size="lg"
          >
            <img src={state.url} alt="Proof of payment, full size" className={styles.full} />
          </Dialog>
        </>
      ) : null}
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
